import { FullCourse } from 'server/types';
import { AsyncForm, FormSubSection, InputLabel, Subtext } from '@components/async-form';
import { rpx } from 'client/lib/rpx-client';
import { BtnPrimary, BtnSecondary, Button } from '@components/buttons';
import { showToast } from '@components/toaster';
import { HeadingSecondary } from '@course/components/headings';
import { useAsyncState } from 'client/lib/hooks';
import { Spinner } from '@components/spinner';
import { useState } from 'preact/hooks';
import { Toggle } from '@components/toggle';
import { IcoChevronDown, IcoChevronUp, IcoPlus, IcoTrash } from '@components/icons';
import { generateUUID } from 'shared/utils';
import type { CheckinPrompt } from 'server/types/public-overrides';
import type { CheckinSendTo } from 'shared/checkin-feedback';
import { lastWeekNumber, mondayOf, summaryWindow } from 'shared/checkin-weeks';
import { fmtReminderHour } from 'shared/checkin-days';
import dayjs from 'dayjs';

const store = rpx.courseSettings;

const MAX_PROMPTS = 5;
const MAX_PROMPT_LENGTH = 200;

/** ISO day numbers, Monday = 1 through Sunday = 7, in the order shown. */
const WEEK_DAYS: Array<{ iso: number; label: string; short: string }> = [
  { iso: 1, label: 'Monday', short: 'Mon' },
  { iso: 2, label: 'Tuesday', short: 'Tue' },
  { iso: 3, label: 'Wednesday', short: 'Wed' },
  { iso: 4, label: 'Thursday', short: 'Thu' },
  { iso: 5, label: 'Friday', short: 'Fri' },
  { iso: 6, label: 'Saturday', short: 'Sat' },
  { iso: 7, label: 'Sunday', short: 'Sun' },
];

export function fmtCheckinDays(days: number[]): string {
  const names = WEEK_DAYS.filter((d) => days.includes(d.iso)).map((d) => d.label);
  if (names.length === 7) {
    return 'every day';
  }
  if (names.length === 5 && !days.includes(6) && !days.includes(7)) {
    return 'Monday to Friday';
  }
  if (names.length <= 1) {
    return names[0] || '';
  }
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

type Props = {
  course: FullCourse;
  onUpdate?: (patch: Partial<FullCourse>) => void;
};

export function CheckinSettings({ course, onUpdate }: Props) {
  const [settings, setSettings] = useAsyncState(
    () => store.getCheckinSettings({ courseId: course.id }),
    [course.id],
  );

  const [enabled, setEnabled] = useState<boolean | undefined>(undefined);
  const [prompts, setPrompts] = useState<CheckinPrompt[] | undefined>(undefined);
  const [startDate, setStartDate] = useState<string | undefined>(undefined);
  const [endDate, setEndDate] = useState<string | undefined>(undefined);
  const [notifyEmail, setNotifyEmail] = useState<string | undefined>(undefined);
  const [remindersEnabled, setRemindersEnabled] = useState<boolean | undefined>(undefined);
  const [reminderHour, setReminderHour] = useState<number | undefined>(undefined);
  const [days, setDays] = useState<number[] | undefined>(undefined);
  const [summariesEnabled, setSummariesEnabled] = useState<boolean | undefined>(undefined);
  const [sprintSummary, setSprintSummary] = useState<boolean | undefined>(undefined);
  const [summaryReminder, setSummaryReminder] = useState<boolean | undefined>(undefined);
  const [replyEmailsEnabled, setReplyEmailsEnabled] = useState<boolean | undefined>(undefined);
  const [sendTo, setSendTo] = useState<CheckinSendTo | undefined>(undefined);
  const [defaultCoachId, setDefaultCoachId] = useState<string | undefined>(undefined);
  const [validationError, setValidationError] = useState<string | undefined>(undefined);
  const [isSaving, setIsSaving] = useState(false);

  if (!settings) {
    return (
      <div class="flex justify-center">
        <Spinner class="border-sky-500 w-8 h-8" />
      </div>
    );
  }

  const currentEnabled = enabled ?? settings.checkinsEnabled;
  const currentPrompts = prompts ?? settings.prompts;
  const currentStartDate = startDate ?? settings.startDate ?? '';
  const currentEndDate = endDate ?? settings.endDate ?? '';
  const currentNotifyEmail = notifyEmail ?? settings.notifyEmail ?? '';
  const currentRemindersEnabled = remindersEnabled ?? settings.remindersEnabled;
  const currentReminderHour = reminderHour ?? settings.reminderHour;
  const currentDays = days ?? settings.days;
  const currentSummariesEnabled = summariesEnabled ?? settings.summariesEnabled;
  const currentSprintSummary = sprintSummary ?? settings.sprintSummary;
  const currentSummaryReminder = summaryReminder ?? settings.summaryReminder;
  const currentReplyEmailsEnabled = replyEmailsEnabled ?? settings.replyEmailsEnabled;
  const currentSendTo = sendTo ?? settings.sendTo;
  const currentDefaultCoachId = defaultCoachId ?? settings.defaultCoachId ?? '';
  const schedule = {
    startDate: currentStartDate || undefined,
    endDate: currentEndDate || undefined,
    sprintSummary: currentSprintSummary,
  };
  const lastWeek = lastWeekNumber(schedule);
  // Shown before the switch is on too, so build it as the Sprint week.
  const sprintWindow = lastWeek
    ? summaryWindow(lastWeek, { ...schedule, sprintSummary: true })
    : undefined;

  function toggleDay(iso: number) {
    setDays(
      currentDays.includes(iso)
        ? currentDays.filter((d) => d !== iso)
        : [...currentDays, iso].sort((a, b) => a - b),
    );
  }

  function updatePrompt(index: number, text: string) {
    setPrompts(currentPrompts.map((p, i) => (i === index ? { ...p, text } : p)));
  }

  function addPrompt() {
    if (currentPrompts.length >= MAX_PROMPTS) {
      return;
    }
    setPrompts([...currentPrompts, { id: generateUUID(), text: '' }]);
  }

  function removePrompt(index: number) {
    if (currentPrompts.length <= 1) {
      return;
    }
    setPrompts(currentPrompts.filter((_, i) => i !== index));
  }

  function movePrompt(index: number, offset: number) {
    const target = index + offset;
    if (target < 0 || target >= currentPrompts.length) {
      return;
    }
    const next = [...currentPrompts];
    const [moved] = next.splice(index, 1);
    next.splice(target, 0, moved);
    setPrompts(next);
  }

  async function submit() {
    if (!currentDays.length) {
      setValidationError('Choose at least one day for standups.');
      return;
    }

    const blankIndex = currentPrompts.findIndex((p) => !p.text.trim());
    if (blankIndex >= 0) {
      setValidationError(
        `Prompt ${blankIndex + 1} is empty. Write a question or remove the prompt.`,
      );
      return;
    }

    setValidationError(undefined);
    setIsSaving(true);

    try {
      const saved = await store.setCheckinSettings({
        courseId: course.id,
        checkinsEnabled: currentEnabled,
        prompts: currentPrompts.map((p) => ({ ...p, text: p.text.trim() })),
        days: currentDays,
        startDate: currentStartDate || undefined,
        endDate: currentEndDate || undefined,
        notifyEmail: currentNotifyEmail,
        remindersEnabled: currentRemindersEnabled,
        reminderHour: currentReminderHour,
        summariesEnabled: currentSummariesEnabled,
        sprintSummary: currentSprintSummary,
        summaryReminder: currentSummaryReminder,
        replyEmailsEnabled: currentReplyEmailsEnabled,
        sendTo: currentSendTo,
        defaultCoachId: (currentDefaultCoachId || null) as UUID | null,
      });

      setSettings(saved);
      onUpdate?.({ checkinsEnabled: saved.checkinsEnabled });

      setEnabled(undefined);
      setPrompts(undefined);
      setStartDate(undefined);
      setEndDate(undefined);
      setNotifyEmail(undefined);
      setRemindersEnabled(undefined);
      setReminderHour(undefined);
      setDays(undefined);
      setSummariesEnabled(undefined);
      setSprintSummary(undefined);
      setSummaryReminder(undefined);
      setReplyEmailsEnabled(undefined);
      setSendTo(undefined);
      setDefaultCoachId(undefined);

      showToast({
        title: 'Settings saved',
        message: 'Standup settings have been updated.',
        type: 'ok',
      });
    } catch (err) {
      // Boom.badRequest serializes a statusCode; a schema rejection serializes
      // type: 'validation' with no statusCode, and puts the per-field message
      // (including our .refine copy) in data.errors rather than in message,
      // which is the generic 'Validation failed'. Anything else is a transport
      // or server failure and belongs in the error dialog, not this banner.
      const { statusCode, type, message, data } = (err || {}) as {
        statusCode?: number;
        type?: string;
        message?: string;
        data?: { errors?: { message?: string }[] };
      };
      const detail = data?.errors?.find((e) => e.message)?.message;
      if ((detail || message) && (statusCode === 400 || type === 'validation')) {
        setValidationError(detail || message);
        return;
      }
      throw err;
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <AsyncForm class="flex flex-col gap-8" onSubmit={submit}>
      <div class="bg-white rounded-lg border border-gray-200 p-6 flex flex-col gap-5">
        <div class="flex flex-col gap-1">
          <HeadingSecondary>Standups</HeadingSecondary>
          <Subtext>
            Students answer a few short questions on each standup day. Their answers land on the
            course home page, in their standup history, and in an email to you.
          </Subtext>
        </div>

        <label class="flex items-start gap-3 cursor-pointer">
          <Toggle checked={currentEnabled} onClick={() => setEnabled(!currentEnabled)} />
          <span class="flex flex-col leading-snug">
            <strong>Turn on standups</strong>
            <span class="opacity-75 text-sm">
              Adds a "Today's standup" card to the course home page and a "Standups" tab to the
              course navigation.
            </span>
          </span>
        </label>
      </div>

      <div class="bg-white rounded-lg border border-gray-200 p-6 flex flex-col gap-5">
        <FormSubSection>
          <InputLabel>Prompts</InputLabel>
          <Subtext>
            Ask between one and five short questions. Keep each one under 200 characters.
          </Subtext>
          <div class="flex flex-col gap-2 pt-1">
            {currentPrompts.map((prompt, index) => (
              <div key={prompt.id} class="flex items-center gap-2">
                <input
                  class="ruz-input grow"
                  type="text"
                  value={prompt.text}
                  maxLength={MAX_PROMPT_LENGTH}
                  placeholder="e.g. What will you work on today?"
                  onInput={(e) => updatePrompt(index, (e.target as HTMLInputElement).value)}
                />
                <Button
                  type="button"
                  class="p-2 rounded-md text-gray-500 hover:bg-gray-100 disabled:opacity-30"
                  disabled={index === 0}
                  onClick={() => movePrompt(index, -1)}
                >
                  <IcoChevronUp class="w-4 h-4" />
                </Button>
                <Button
                  type="button"
                  class="p-2 rounded-md text-gray-500 hover:bg-gray-100 disabled:opacity-30"
                  disabled={index === currentPrompts.length - 1}
                  onClick={() => movePrompt(index, 1)}
                >
                  <IcoChevronDown class="w-4 h-4" />
                </Button>
                <Button
                  type="button"
                  class="p-2 rounded-md text-gray-500 hover:bg-gray-100 disabled:opacity-30"
                  disabled={currentPrompts.length <= 1}
                  onClick={() => removePrompt(index)}
                >
                  <IcoTrash class="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
          {currentPrompts.length < MAX_PROMPTS && (
            <div class="pt-2">
              <BtnSecondary type="button" onClick={addPrompt}>
                <IcoPlus class="w-4 h-4 mr-1" />
                Add a prompt
              </BtnSecondary>
            </div>
          )}
        </FormSubSection>
      </div>

      <div class="bg-white rounded-lg border border-gray-200 p-6 flex flex-col gap-5">
        <HeadingSecondary>Active window</HeadingSecondary>
        <FormSubSection>
          <InputLabel>Standup days</InputLabel>
          <div class="flex flex-wrap gap-2">
            {WEEK_DAYS.map((day) => {
              const on = currentDays.includes(day.iso);
              return (
                <Button
                  key={day.iso}
                  type="button"
                  aria-pressed={on}
                  class={`px-3 py-1.5 rounded-full border text-sm ${
                    on
                      ? 'bg-indigo-600 border-indigo-600 text-white'
                      : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-100'
                  }`}
                  onClick={() => toggleDay(day.iso)}
                >
                  {day.short}
                </Button>
              );
            })}
          </div>
          <Subtext>
            {currentDays.length
              ? `Students are asked for a standup on ${fmtCheckinDays(currentDays)}.`
              : 'Choose at least one day.'}
          </Subtext>
          <p class="text-sm text-amber-800">
            Set the days before the first standup. Changing them later changes the past marks in the
            Grid view.
          </p>
        </FormSubSection>
        <Subtext>
          Leave both dates empty to collect standups for as long as the course runs.
        </Subtext>
        <div class="flex flex-col md:flex-row gap-4">
          <FormSubSection class="grow">
            <InputLabel>Start date</InputLabel>
            <input
              class="ruz-input"
              type="date"
              value={currentStartDate}
              onInput={(e) => setStartDate((e.target as HTMLInputElement).value)}
            />
          </FormSubSection>
          <FormSubSection class="grow">
            <InputLabel>End date</InputLabel>
            <input
              class="ruz-input"
              type="date"
              value={currentEndDate}
              onInput={(e) => setEndDate((e.target as HTMLInputElement).value)}
            />
          </FormSubSection>
        </div>
      </div>

      <div class="bg-white rounded-lg border border-gray-200 p-6 flex flex-col gap-5">
        <HeadingSecondary>Weekly summary</HeadingSecondary>
        <label class={`flex items-start gap-3 ${currentEnabled ? 'cursor-pointer' : 'opacity-60'}`}>
          <Toggle
            checked={currentSummariesEnabled && currentEnabled}
            disabled={!currentEnabled}
            onClick={() => setSummariesEnabled(!currentSummariesEnabled)}
          />
          <span class="flex flex-col leading-snug">
            <strong>Collect a weekly summary</strong>
            <span class="opacity-75 text-sm">
              A separate box on the Standups page. Students paste the coach summary from their
              weekly review, up to 10,000 characters.
            </span>
          </span>
        </label>
        {currentSummariesEnabled && currentEnabled && (
          <>
            <dl class="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-sm">
              <dt class="font-semibold text-gray-700">Week 1 starts</dt>
              <dd>
                {currentStartDate
                  ? dayjs(mondayOf(currentStartDate)).format('ddd, MMM D, YYYY')
                  : 'Set a start date to number the weeks.'}
              </dd>
              <dt class="font-semibold text-gray-700">Each week</dt>
              <dd>Opens Friday · due by the end of Sunday, in each student’s time zone</dd>
              <dt class="font-semibold text-gray-700">Late</dt>
              <dd>Accepted until the next summary opens, marked “Sent late” for coaches</dd>
            </dl>
            <label class={`flex items-start gap-3 ${lastWeek ? 'cursor-pointer' : 'opacity-60'}`}>
              <Toggle
                checked={currentSprintSummary && !!lastWeek}
                disabled={!lastWeek}
                onClick={() => setSprintSummary(!currentSprintSummary)}
              />
              <span class="flex flex-col leading-snug">
                <strong>Collect a Sprint summary in the last week</strong>
                <span class="opacity-75 text-sm">
                  {sprintWindow
                    ? `Week ${sprintWindow.weekNumber} · opens ${dayjs(sprintWindow.opensOn).format(
                        'ddd, MMM D',
                      )} · due before each student’s coaching call`
                    : 'Set a start and end date to have a last week.'}
                </span>
              </span>
            </label>
            <label
              class={`flex items-start gap-3 ${
                currentRemindersEnabled ? 'cursor-pointer' : 'opacity-60'
              }`}
            >
              <Toggle
                checked={currentSummaryReminder && currentRemindersEnabled}
                disabled={!currentRemindersEnabled}
                onClick={() => setSummaryReminder(!currentSummaryReminder)}
              />
              <span class="flex flex-col leading-snug">
                <strong>Remind students who haven’t sent their summary</strong>
                <span class="opacity-75 text-sm">
                  {currentRemindersEnabled
                    ? 'Sunday, at the daily reminder time'
                    : 'Turn on the daily reminder to send this.'}
                </span>
              </span>
            </label>
          </>
        )}
      </div>

      <div class="bg-white rounded-lg border border-gray-200 p-6 flex flex-col gap-5">
        <HeadingSecondary>Coaches</HeadingSecondary>
        <FormSubSection>
          <InputLabel>Default coach</InputLabel>
          <Subtext>
            Students you haven’t assigned count as this coach’s. Change one student’s coach on the
            Students screen, under Standups.
          </Subtext>
          <select
            class="inline-ruz-input"
            value={currentDefaultCoachId}
            onChange={(e) => setDefaultCoachId((e.target as HTMLSelectElement).value)}
          >
            <option value="">No default coach</option>
            {settings.coaches.map((coach) => (
              <option key={coach.id} value={coach.id}>
                {coach.name}
              </option>
            ))}
          </select>
        </FormSubSection>
      </div>

      <div class="bg-white rounded-lg border border-gray-200 p-6 flex flex-col gap-5">
        <HeadingSecondary>Emails</HeadingSecondary>
        <FormSubSection>
          <InputLabel>Send new standups and summaries to</InputLabel>
          <label class="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="radio"
              name="checkin-send-to"
              checked={currentSendTo === 'address'}
              onChange={() => setSendTo('address')}
            />
            One address
          </label>
          <label class="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="radio"
              name="checkin-send-to"
              checked={currentSendTo === 'coach'}
              onChange={() => setSendTo('coach')}
            />
            Each student’s coach
          </label>
          <Subtext>
            {currentSendTo === 'coach'
              ? 'Students without a coach go to the address below, or to the guide and facilitators if it’s empty.'
              : 'Each email has a React or reply button. Replying to the email reaches the student directly.'}
          </Subtext>
        </FormSubSection>
        <FormSubSection>
          <InputLabel>Address</InputLabel>
          <Subtext>
            Leave empty to email the course guide and facilitators. Enter a helpdesk mailbox address
            (e.g. FreeScout) to get one conversation per student.
          </Subtext>
          <input
            class="ruz-input"
            type="email"
            value={currentNotifyEmail}
            placeholder="standups@example.com"
            onInput={(e) => setNotifyEmail((e.target as HTMLInputElement).value)}
          />
        </FormSubSection>
        <label class="flex items-start gap-3 cursor-pointer">
          <Toggle
            checked={currentReplyEmailsEnabled}
            onClick={() => setReplyEmailsEnabled(!currentReplyEmailsEnabled)}
          />
          <span class="flex flex-col leading-snug">
            <strong>Email students when a coach replies</strong>
            <span class="opacity-75 text-sm">
              Sent 10 minutes after the reply, so a coach can still fix or delete it. Reactions show
              in the course only. Students who reply to the email reach{' '}
              {currentSendTo === 'coach'
                ? 'their coach'
                : currentNotifyEmail || 'the replying coach'}
              .
            </span>
          </span>
        </label>
      </div>

      <div class="bg-white rounded-lg border border-gray-200 p-6 flex flex-col gap-5">
        <HeadingSecondary>Daily reminder</HeadingSecondary>

        <label class={`flex items-start gap-3 ${currentEnabled ? 'cursor-pointer' : 'opacity-60'}`}>
          <Toggle
            checked={currentRemindersEnabled && currentEnabled}
            disabled={!currentEnabled}
            onClick={() => setRemindersEnabled(!currentRemindersEnabled)}
          />
          <span class="flex flex-col leading-snug">
            <strong>Email a reminder to students who have not submitted their standup</strong>
            <span class="opacity-75 text-sm">
              {currentEnabled
                ? 'One email per standup day, skipped for anyone who already submitted their standup that day.'
                : 'Turn daily standups on to send reminders.'}
            </span>
          </span>
        </label>

        <FormSubSection>
          <InputLabel>Reminder time</InputLabel>
          <Subtext>
            Sent in each student's local time zone to anyone who hasn't submitted a standup yet.
          </Subtext>
          <select
            class="inline-ruz-input"
            value={String(currentReminderHour)}
            disabled={!currentEnabled || !currentRemindersEnabled}
            onChange={(e) => setReminderHour(Number((e.target as HTMLSelectElement).value))}
          >
            {Array.from({ length: 24 }, (_, hour) => (
              <option key={hour} value={String(hour)}>
                {fmtReminderHour(hour)}
              </option>
            ))}
          </select>
        </FormSubSection>
      </div>

      {validationError && (
        <div class="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {validationError}
        </div>
      )}

      <footer>
        <BtnPrimary disabled={isSaving}>{isSaving ? 'Saving…' : 'Save'}</BtnPrimary>
      </footer>
    </AsyncForm>
  );
}
